import React, { useEffect } from 'react';
import TextField from '@mui/material/TextField';
import { Controller, UseControllerProps } from 'react-hook-form';

interface ControlledTextAreaProps {
  label: string;
  id: string;
  name: string;
  className?: string;
  variant?: "standard" | "filled" | "outlined";
  disabled?: boolean;
  control: any;
  defaultValue?: any;
  rules?: UseControllerProps['rules'];
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onTextChange?: (event: any) => void;
  required?: boolean;
  setValue?: any;
  register?: any;
  maxLength?: number;
  minLength?: number;
  rows?: number;
  hide?: boolean;
  size?: 'small' | 'medium',
  bulletType?: "none" | "bullet" | "dash" | "number" | "star";
}

const WSTextAreaField: React.FC<ControlledTextAreaProps> = ({
  id,
  label,
  className,
  control,
  name,
  rules,
  variant = "outlined",
  disabled = false,
  defaultValue = '',
  setValue,
  onChange,
  onTextChange = () => { },
  maxLength,
  minLength,
  rows = 4,
  hide = false,
  size = "medium",
  bulletType = "none",
}) => {
  useEffect(() => {
    // Custom logic (if needed)
  }, []);

  const getPrefix = (index: number) => {
    switch (bulletType) {
      case "bullet":
        return "• ";
      case "dash":
        return "- ";
      case "star":
        return "* ";
      case "number":
        return `${index}. `;
      default:
        return "";
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>,
    field: any
  ) => {
    if (e.key === "Enter" && bulletType !== "none") {
      e.preventDefault();

      const textarea = e.target as HTMLTextAreaElement;
      const { selectionStart, value } = textarea;

      const lines = value.split("\n");
      const currentLineIndex =
        value.substring(0, selectionStart).split("\n").length - 1;

      // find next prefix
      const nextIndex =
        bulletType === "number" ? lines.length + 1 : currentLineIndex + 1;

      const prefix = getPrefix(nextIndex);

      const newValue =
        value.substring(0, selectionStart) + "\n" + prefix + value.substring(selectionStart);

      field.onChange({ target: { name, value: newValue } });

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd =
          selectionStart + prefix.length + 1;
      }, 0);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: any
  ) => {
    let value = e.target.value;

    if (
      bulletType !== "none" &&
      value.trim() !== "" &&
      !value.startsWith(getPrefix(1))
    ) {
      // add prefix if missing
      value = getPrefix(1) + value;
    }

    field.onChange({ target: { name, value } });
  };


  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState }) => {
        return (
          <TextField
            id={id}
            label={label}
            fullWidth
            multiline
            rows={rows}
            size={size}
            disabled={disabled}
            error={!!fieldState.error}
            helperText={fieldState.error?.message || ""}
            value={field.value}
            onChange={(e) => handleChange(e, field)}
            onKeyDown={(e) => handleKeyDown(e, field)}
            variant="outlined"
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "0.875rem",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "0.75rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.875rem",
              },
            }}
          />
        );
      }}
    />
  );
};

export default WSTextAreaField;
